import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "../../lib/apiClient";
import { Users, Bell, CalendarCheck, TrendingUp, Loader2, PoundSterling } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, PieChart, Pie, Tooltip } from "recharts";
import { CreditsCard } from "../../components/CreditsCard";
import { OwnerBalanceStrip } from "../../components/OwnerBalanceStrip";

const statusColor = { hot: "#f97316", warm: "#eab308", cold: "#3b82f6", new: "#71717a" };

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-[#18181b] border border-white/10 rounded-sm p-4 sm:p-6 hover:border-primary/40 transition-[border-color] duration-200" data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}>
    <div className="flex items-center justify-between mb-3 sm:mb-4 gap-2">
      <span className="text-[11px] sm:text-xs uppercase tracking-wider text-muted-foreground leading-tight">{label}</span>
      <div className="h-8 w-8 rounded-sm flex items-center justify-center shrink-0" style={{ backgroundColor: `${accent}22` }}>
        <Icon className="h-4 w-4" style={{ color: accent }} />
      </div>
    </div>
    <p className="font-display text-3xl sm:text-4xl tracking-tight">{value}</p>
  </div>
);

export default function Overview() {
  const { company } = useOutletContext();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const pie = Object.entries(stats.status_breakdown).map(([k, v]) => ({ name: k, value: v }));

  return (
    <div className="space-y-8" data-testid="overview-page">
      <OwnerBalanceStrip isOwner={!!company?.free_access} />
      <div>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight">COMMAND CENTER</h1>
        <p className="text-muted-foreground mt-1">Every lead Ava captured, at a glance.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard icon={Users} label="Total Leads" value={stats.total_leads} accent="#f97316" />
        <StatCard icon={Bell} label="New Requests" value={stats.new_requests} accent="#eab308" />
        <StatCard icon={CalendarCheck} label="Jobs Booked" value={stats.jobs_scheduled} accent="#22c55e" />
        <StatCard icon={TrendingUp} label="Conversion" value={`${stats.conversion_rate}%`} accent="#3b82f6" />
      </div>

      <CreditsCard />

      {stats.analytics_enabled === false ? (
      <div className="bg-[#18181b] border border-white/10 rounded-sm p-6" data-testid="analytics-locked">
        <h3 className="font-display text-xl tracking-wide mb-1">PROFIT TRACKING</h3>
        <p className="text-sm text-muted-foreground">Profit &amp; earnings tracking and the 6-month chart are a <span className="text-primary font-medium">Pro</span> feature. Upgrade on the Billing tab to see how much you're making each month.</p>
      </div>
      ) : (
      <div className="bg-gradient-to-br from-[#1a1410] to-[#18181b] border border-primary/30 rounded-sm p-6" data-testid="earnings-card">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-sm bg-primary/15 flex items-center justify-center"><PoundSterling className="h-5 w-5 text-primary" /></div>
            <h3 className="font-display text-xl tracking-wide">THIS MONTH · {stats.month_label?.toUpperCase()}</h3>
          </div>
          <span className="text-xs text-muted-foreground">{stats.month_completed_jobs} job{stats.month_completed_jobs !== 1 ? "s" : ""} completed</span>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Earned</p>
            <p className="font-display text-3xl md:text-4xl tracking-tight" data-testid="month-revenue">£{(stats.month_revenue || 0).toLocaleString()}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Materials</p>
            <p className="font-display text-3xl md:text-4xl tracking-tight text-muted-foreground" data-testid="month-materials">£{(stats.month_materials || 0).toLocaleString()}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Profit</p>
            <p className="font-display text-3xl md:text-4xl tracking-tight text-primary" data-testid="month-profit">£{(stats.month_profit || 0).toLocaleString()}</p>
          </div>
        </div>
        {stats.month_completed_jobs === 0 && <p className="text-xs text-muted-foreground mt-4">Mark a booking as completed and pop in the price + material cost to see your profit build up here.</p>}
      </div>
      )}

      {stats.monthly_series && stats.monthly_series.some((m) => m.profit || m.revenue) && (
        <div className="bg-[#18181b] border border-white/10 rounded-sm p-6" data-testid="profit-trend-card">
          <h3 className="font-display text-xl tracking-wide mb-6">PROFIT · LAST 6 MONTHS</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={stats.monthly_series}>
              <XAxis dataKey="month" stroke="#71717a" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="#71717a" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => `£${v}`} />
              <Tooltip cursor={{ fill: "#ffffff08" }} contentStyle={{ background: "#0f0f11", border: "1px solid #ffffff20", borderRadius: 4, color: "#fff" }} formatter={(v, n) => [`£${v.toLocaleString()}`, n === "profit" ? "Profit" : "Revenue"]} />
              <Bar dataKey="profit" radius={[2, 2, 0, 0]} fill="#f97316" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-[#18181b] border border-white/10 rounded-sm p-6">
          <h3 className="font-display text-xl tracking-wide mb-6">LEADS BY TRADE</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={stats.by_trade}>
              <XAxis dataKey="trade" stroke="#71717a" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="#71717a" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
              <Bar dataKey="count" radius={[2, 2, 0, 0]}>
                {stats.by_trade.map((_, i) => <Cell key={i} fill="#f97316" />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-[#18181b] border border-white/10 rounded-sm p-6">
          <h3 className="font-display text-xl tracking-wide mb-4">LEAD STATUS</h3>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={pie} dataKey="value" nameKey="name" innerRadius={45} outerRadius={70} paddingAngle={2}>
                {pie.map((e, i) => <Cell key={i} fill={statusColor[e.name]} />)}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-2 mt-4">
            {pie.map((e) => (
              <div key={e.name} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 capitalize text-muted-foreground">
                  <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: statusColor[e.name] }} /> {e.name}
                </span>
                <span className="font-semibold">{e.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-[#18181b] border border-white/10 rounded-sm p-6">
        <h3 className="font-display text-xl tracking-wide mb-4">RECENT LEADS</h3>
        {stats.recent_leads.length === 0 ? (
          <p className="text-muted-foreground text-sm py-6 text-center">No leads yet. Try the <a href="/c/demo" className="text-primary underline">live demo</a> to generate one.</p>
        ) : (
          <div className="space-y-2">
            {stats.recent_leads.map((l) => (
              <div key={l.id} className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
                <div>
                  <p className="font-medium">{l.name || "Unknown caller"}</p>
                  <p className="text-xs text-muted-foreground">{l.trade || "—"} · {l.job_description || l.summary || "No details"}</p>
                </div>
                <span className="text-xs uppercase tracking-wide px-2.5 py-1 rounded-sm font-semibold" style={{ backgroundColor: `${statusColor[l.status]}22`, color: statusColor[l.status] }}>
                  {l.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
